import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Package, Award, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IMAGES } from '@/assets/images';

// Birincil Alüminyum tabloları (mechem.com kaynaklı)
const standardPurityTable = [
  { alloy: 'P0406', si: '0.04', fe: '0.06', zn: '0.03', v: '0.02', ga: '0.03', al: '99.85', othersEach: '0.02', othersTotal: '0.05' },
  { alloy: 'P0610', si: '0.06', fe: '0.10', zn: '0.015', v: '0.010', ga: '0.009', al: '99.80', othersEach: '0.02', othersTotal: '0.05' },
  { alloy: 'P1020', si: '0.10', fe: '0.20', zn: '0.03', v: '0.03', ga: '0.04', al: '99.70', othersEach: '0.03', othersTotal: '0.10' },
];

const highPurityTable = [
  { alloy: 'P0101', si: '0.0150', fe: '0.0120', ti: '0.0050', zn: '0.0020', cr: '0.0010', na: '0.0020', v: '0.0024', b: '0.0020', ga: '0.0070', ca: '0.0020', ni: '0.0020', li: '0.0005', al: '99.95', othersEach: '0.0020', othersTotal: '0.0100' },
  { alloy: 'P0202', si: '0.020', fe: '0.020', ti: '0.020', zn: '0.010', cr: '0.020', na: '-', v: '-', b: '-', ga: '-', ca: '-', ni: '-', li: '-', al: '99.90', othersEach: '0.010', othersTotal: '0.020' },
  { alloy: 'P0303', si: '0.030', fe: '0.030', ti: '0.003', zn: '0.01', cr: '0.03', na: '-', v: '-', b: '-', ga: '-', ca: '-', ni: '-', li: '-', al: '99.85', othersEach: '0.010', othersTotal: '0.020' },
  { alloy: 'P0404', si: '0.040', fe: '0.040', ti: '0.003', zn: '0.0100', cr: '0.01', na: '0.03', v: '0.0100', b: '-', ga: '-', ca: '-', ni: '-', li: '-', al: '99.85', othersEach: '0.01', othersTotal: '0.03' },
];

const aSeriesTable = [
  { alloy: 'A6', si: '0.18', fe: '0.25', mn: '0.03', mg: '0.03', cu: '0.01', ti: '0.02', zn: '0.05', sn: '0.03', ga: '99.60', pb: '-', al: '-' },
  { alloy: 'A5', si: '0.25', fe: '0.30', mn: '0.05', mg: '0.03', cu: '0.02', ti: '0.02', zn: '0.06', sn: '0.03', ga: '99.50', pb: '-', al: '-' },
  { alloy: 'A35', si: '0.25', fe: '0.40', mn: '0.05', mg: '0.05', cu: '0.05', ti: '0.02', zn: '0.1', sn: '-', ga: '99.35', pb: '-', al: '-' },
  { alloy: 'A0', si: '0.25', fe: '0.50', mn: '0.05', mg: '0.05', cu: '0.03', ti: '0.02', zn: '0.10', sn: '-', ga: '99.00', pb: '-', al: '-' },
  { alloy: 'AV97', si: '1.00', fe: '2.50', mn: '0.1', mg: '0.1', cu: '0.1', ti: '0.1', zn: '0.1', sn: '-', ga: '97.00', pb: '-', al: '-' },
];

const ecGradeTable = [
  { alloy: 'A7 EC Grade', si: '0.08', fe: '0.20', mn: '0.01', mg: '0.01', cu: '0.01', ti: '0.003', zn: '0.03', cr: '0.003', v: '0.003', b: '0.02', ga: '0.02', pb: '0.01', ni: '0.01', al: '99.70' },
];

// Birincil Alaşımlı Alüminyum tabloları (mechem.com kaynaklı)
const alloyedBilletTable = [
  { alloy: '6060HP', si: '0.40–0.44', fe: '0.15–0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.36–0.40', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6060-30', si: '0.40–0.45', fe: '0.15–0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.40–0.45', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6060-30s', si: '0.42-0.47', fe: '0.16-0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.39-0.45', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6060-90', si: '0.50-0.55', fe: '0.16-0.22', cu: '0.02', mn: '0.02-0.03', mg: '0.50-0.55', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6063-35I', si: '0.42–0.46', fe: '0.15–0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.42–0.47', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6063-35S', si: '0.42-0.46', fe: '0.14-0.19', cu: '0.02', mn: '0.01-0.03', mg: '0.46-0.50', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6005', si: '0.58-0.63', fe: '0.18-0.22', cu: '0.10-0.2', mn: '0.13-0.17', mg: '0.53-0.58', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '-', othersTotal: '0.10' },
  { alloy: '6061', si: '0.61-0.66', fe: '0.14-0.20', cu: '0.20-0.30', mn: 'maks 0.05', mg: '0.90-0.96', zn: '0.02', cr: '0.06-0.19', ti: '0.02-0.05', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6082', si: '0.95-1.05', fe: '0.15-0.23', cu: 'maks 0.03', mn: '0.50-0.57', mg: '0.62-0.67', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '-', othersTotal: '0.10' },
  { alloy: '6463', si: '0.34-0.38', fe: '0.06-0.13', cu: '0.10-0.15', mn: '0.03-0.05', mg: '0.45-0.53', zn: '0.02', cr: '0.03', ti: '0.05', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: 'P1020', si: 'Maks 0.08', fe: 'Maks 0.20', cu: 'Maks 0.01', mn: 'Maks 0.01', mg: 'Maks 0.02', zn: '0.04', cr: 'Maks 0.01', ti: 'Maks 0.01', othersEach: '0.02', othersTotal: '0.10' },
];

const slabTable = [
  { thickness: '600', width: '1630', length: '3800' },
  { thickness: '600', width: '1630', length: '3020' },
  { thickness: '600', width: '1630', length: '4115' },
  { thickness: '600', width: '1750', length: '3810' },
  { thickness: '560', width: '1310', length: '4500' },
  { thickness: '560', width: '1100', length: '4000' },
];

const castingIngotsTable = [
  { alloy: 'AlSi10Mg', si: '9.00-11.00', fe: '1.00', mn: '0.001-0.4', mg: '0.2-0.5', cu: '0.1', ti: '0.15', zn: '0.1', cr: '0.05', sn: '-', sr: '-', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi7MgSr', si: '6.60-7.30', fe: '0.13', mn: '0.05', mg: '0.25-0.33', cu: '0.02', ti: '0.10-0.15', zn: '0.05', cr: '0.005', sn: '0.01', sr: '0.013-0.023', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi11MgSr', si: '10.70-11.60', fe: '0.1300', mn: '0.04', mg: '0.12-0.17', cu: '0.02', ti: '0.10-0.15', zn: '0.04', cr: '0.005', sn: '0.01', sr: '0.0350-0.0450', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi10MgMn', si: '9.50-11.50', fe: '0.15', mn: '0.60-0.80', mg: '0.15-0.30', cu: '0.03', ti: '0.04-0.15', zn: '0.07', cr: '0.03', sn: '0.005', sr: '0.015-0.030', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: '145-piston alaşımı', si: '11.00-12.00', fe: '0.20-0.30', mn: '0.15', mg: '0.8-1.40', cu: '0.80-1.5', ti: '0.08', zn: '0.02', cr: '0.05', sn: '-', sr: '-', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: '175-piston alaşımı', si: '11.00-12.00', fe: '0.20-0.30', mn: '0.15', mg: '0.80-1.40', cu: '3.00-4.50', ti: '0.05', zn: '0.08', cr: '0.05', sn: '2.00-3.00', sr: '0.001-0.004', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi9Cu3', si: '8.00-9.50', fe: '0.70-1.00', mn: '0.40-0.65', mg: '0.15-0.40', cu: '3.00-3.50', ti: '0.15', zn: '1.00', cr: '0.10', sn: '0.0050', sr: '-', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi50', si: '47-53', fe: '0.30', mn: '0.05', mg: 'Maks 0.05', cu: '0.05', ti: '0.05', zn: '0.05', cr: '0.05', sn: '0.15', sr: '-', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi20', si: '18-22', fe: '0.45', mn: '0.10', mg: '0.10', cu: '0.10', ti: '0.10', zn: '0.10', cr: '-', sn: '-', sr: '-', al: 'denge', othersEach: '0.020', othersTotal: '0.100' },
];

export default function AluminumBillet() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Bölümü */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.ALUMINUM_BILLET_CASTING_PLANT_1_302}
            alt="Alüminyum Billet ve Külçe"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <Link
              to="/tr"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Ana Sayfaya Dön
            </Link>
            <h1 className="text-5xl font-bold text-white mb-6">
              Alüminyum Billet / Ingot
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Birincil alüminyum ve birincil alaşımlı alüminyum ürünleri — ekstrüzyon kütükleri,
              haddeli yassı kütükler ve döküm külçeleri ile tüm endüstriyel ihtiyaçlarınıza çözüm sunuyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Birincil Alüminyum Bölümü */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            {/* Başlık */}
            {/* Tam genişlikte foto */}
            <div className="relative rounded-2xl overflow-hidden mb-10">
              <img
                src={IMAGES.ALUMINYUM_BILLET_NEDIR}
                alt="Alüminyum Billet"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute bottom-4 left-4">
                <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">Birincil Alüminyum</span>
              </div>
            </div>
            <div className="mb-12">
              <div>
                <h2 className="text-3xl font-bold text-primary mb-2">Alüminyum Billet</h2>
                <p className="text-lg text-muted-foreground font-medium mb-4">Birincil Alüminyum</p>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Geniş tedarikçi ağımız ve güçlü destek servisimizle tüm müşterilerimize yüksek saflıkta
                  birincil alüminyum billet temin ediyoruz. Ekstrüzyon, hadde ve döküm uygulamaları için
                  çeşitli alaşım seçenekleri mevcuttur.
                </p>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" /> Tedarik Şekilleri
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">15-24 kg'lık külçe</span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">750 kg'lık T Profil</span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">700 kg'lık pik demir</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tablo 1: Standart Saflık (P serisi) */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Standart Saflık — Kimyasal Kompozisyon (Maks %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-24 kg'lık külçe, 750 kg'lık T Profil ve 700 kg'lık pik demir şekilleri</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alaşım (Maks)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">V %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ga %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                      <th className="text-center px-3 py-2 font-semibold">Diğer (Her)</th>
                      <th className="text-center px-3 py-2 font-semibold">Diğer (Toplam)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standardPurityTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.v}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ga}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tablo 2: Yüksek Saflık (P serisi) */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Yüksek Saflık — Kimyasal Kompozisyon (Maks %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-22 kg'lık külçe ve 600 kg'lık T Profil</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alaşım (Maks)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                      <th className="text-center px-3 py-2 font-semibold">Na %</th>
                      <th className="text-center px-3 py-2 font-semibold">V %</th>
                      <th className="text-center px-3 py-2 font-semibold">B %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ga %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                      <th className="text-center px-3 py-2 font-semibold">Diğer (Her)</th>
                      <th className="text-center px-3 py-2 font-semibold">Diğer (Toplam)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {highPurityTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.na}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.v}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.b}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ga}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tablo 3: A Serisi */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> A Serisi — Kimyasal Kompozisyon (Maks %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-24 kg'lık külçe ve 750 kg'lık T Profil</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alaşım (Maks)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Sn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aSeriesTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.sn}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.ga}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tablo 4: EC Grade */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> EC Grade — Kimyasal Kompozisyon (Maks %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-24 kg'lık külçe, 750 kg'lık T Profil ve 700 kg'lık pik demir şekilleri</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alaşım (Maks)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                      <th className="text-center px-3 py-2 font-semibold">V %</th>
                      <th className="text-center px-3 py-2 font-semibold">B %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ga %</th>
                      <th className="text-center px-3 py-2 font-semibold">Pb %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ni %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ecGradeTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.v}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.b}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ga}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.pb}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ni}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Birincil Alaşımlı Alüminyum Bölümü */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            {/* Bölüm Başlığı */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-primary mb-4">Birincil Alaşımlı Alüminyum</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Farklı endüstrilerde faaliyet gösteren müşterilerimizin birincil alüminyum alaşımlı ürün talepleri
                doğrultusunda ekstrüzyon kütükleri, haddeli yassı kütükler ve döküm külçeleri/T-profilleri sağlıyoruz.
              </p>
            </div>

            {/* Ekstrüzyon Kütükleri */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-primary mb-2">Birincil Alüminyum Ekstrüzyon Kütükleri</h3>
              <p className="text-muted-foreground mb-2">4" (100 mm) ila 17" (430 mm) çapında ve 5800 mm, 6000 mm ve 7000 mm uzunluğunda Alaşımlı kütük (1xxx-7xxx) serisi.</p>
              <p className="text-sm text-muted-foreground mb-6">Ayrıca 2xxx, 3xxx, 6xxx, 5xxx ve 7xxx alaşımlarında müşteri taleplerine uygun parçalar tedarik edebiliyoruz.</p>
              <div className="border border-border rounded-xl overflow-hidden mb-6">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary" /> Kimyasal Kompozisyon (Maks %)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alaşım (Maks)</th>
                        <th className="text-center px-3 py-2 font-semibold">Si %</th>
                        <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                        <th className="text-center px-3 py-2 font-semibold">Zn maks %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                        <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                        <th className="text-center px-3 py-2 font-semibold">Diğer (Her)</th>
                        <th className="text-center px-3 py-2 font-semibold">Diğer (Toplam)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alloyedBilletTable.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                          <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Mekanik Özellikler */}
              <div className="border border-border rounded-xl overflow-hidden mb-4">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" /> Mekanik Özellikler
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Özellik</th>
                        <th className="text-center px-4 py-2 font-semibold">T5</th>
                        <th className="text-center px-4 py-2 font-semibold">T6</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-background">
                        <td className="px-4 py-2 font-medium text-primary">Çekme mukavemeti (MPa)</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">160</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">190</td>
                      </tr>
                      <tr className="bg-muted/20">
                        <td className="px-4 py-2 font-medium text-primary">Akma mukavemeti (MPa)</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">120</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">150</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Metalürjik & Fiziksel Kalite */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-5">
                  <h4 className="font-semibold text-primary mb-3">Metalürjik Kalite</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Ortalama tane boyutu: min 2000 tane/cm² (ASTM E112-96 uyarınca maks 200)</li>
                    <li>• Ters ayrıklaştırma bölgesi: &lt;200 µ</li>
                    <li>• İnklüzyonlar seramik köpük filtresiyle filtrelenmelidir (min 30 ppi)</li>
                    <li>• Maks inklüzyon miktarı: 20-50 µ: &lt;7 | 51-100 µ: &lt;1 | &gt;100 µ: 0</li>
                  </ul>
                </div>
                <div className="bg-card border border-border rounded-xl p-5">
                  <h4 className="font-semibold text-primary mb-3">Fiziksel Kalite</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Maks yüzey hasarı: 2 mm derinlik, 5 mm genişlik, 250 mm uzunluk</li>
                    <li>• Toleranslar: 203 mm'ye kadar +0/-2 mm; 228 mm ve üstü +0/-3 mm</li>
                    <li>• İmleme: Döküm numarası ve alaşım kodu ile tek ucu kesik işaretleme</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Haddeli Yassı Kütükler (Slab) */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-primary mb-2">Birincil Alüminyum Haddeli Yassı Kütükler</h3>
              <p className="text-muted-foreground mb-6">1xxx, 3xxx, 5xxx ve 6xxx serisi haddeli yassı kütükler müşteri şartnamesine uygun olarak tedarik edilir. Talep üzerine farklı boyutlarda imalat yapılabilir.</p>
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" /> Slab Ebatları
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-center px-4 py-2 font-semibold">Kalınlık (mm)</th>
                        <th className="text-center px-4 py-2 font-semibold">Genişlik (mm)</th>
                        <th className="text-center px-4 py-2 font-semibold">Uzunluk (mm)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slabTable.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                          <td className="px-4 py-2 text-center font-medium text-primary">{row.thickness}</td>
                          <td className="px-4 py-2 text-center text-muted-foreground">{row.width}</td>
                          <td className="px-4 py-2 text-center text-muted-foreground">{row.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Döküm Külçeleri / T-Profilleri */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-primary mb-2">Birincil Alaşımlı Döküm Külçeleri / T-Profilleri</h3>
              <p className="text-muted-foreground mb-2">EN1706, EN AC42100 uyumlu. Kalite sertifikaları: ISO/TS9001, ISO/TS16949, ISO14001, ISO18000, ISO27000.</p>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">Açık kalıp külçe: 7-15 kg</span>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">Sürekli kalıp külçe: 6-8 kg</span>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">Sürekli döküm barı: 10 kg</span>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">T profili ağırlık: 300-750 kg</span>
              </div>
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary" /> Kimyasal Kompozisyon (Maks %)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alaşım</th>
                        <th className="text-center px-3 py-2 font-semibold">Si %</th>
                        <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                        <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                        <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                        <th className="text-center px-3 py-2 font-semibold">Sn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Sr %</th>
                        <th className="text-center px-3 py-2 font-semibold">Al</th>
                        <th className="text-center px-3 py-2 font-semibold">Diğer (Her)</th>
                        <th className="text-center px-3 py-2 font-semibold">Diğer (Toplam)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {castingIngotsTable.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                          <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.sn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.sr}</td>
                          <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Alüminyum Tel Çubuk Bölümü */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-primary mb-4">Aluminium Wire Rod</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Elektrik ve endüstriyel uygulamalar için çeşitli alaşım sınıflarında yüksek kaliteli alüminyum tel çubuk
                temin ediyoruz. Hassas kimyasal ve mekanik özellik garantili standart bobin formatlarında sunulmaktadır.
              </p>
            </div>

            {/* Kimyasal Kompozisyon Tablo 1: Saf Alaşımlar */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Kimyasal Kompozisyon — Saf Alaşımlar (Maks %)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Alaşım</th>
                      <th className="text-center px-4 py-2 font-semibold">Al içeriği (min %)</th>
                      <th className="text-center px-4 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-4 py-2 font-semibold">Si %</th>
                      <th className="text-center px-4 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-4 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-4 py-2 font-semibold">Ti+V+Mn+Cr %</th>
                      <th className="text-center px-4 py-2 font-semibold">Diğer (Her) %</th>
                      <th className="text-center px-4 py-2 font-semibold">Diğer (Toplam) %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { alloy: '1080', al: '99.8', fe: '0.12', si: '0.10', cu: '0.01', zn: '0.04', tiVMnCr: '0.01', otherEach: '0.02', otherTotal: '0.20' },
                      { alloy: '1370', al: '99.7', fe: '0.25', si: '0.10', cu: '0.02', zn: '0.04', tiVMnCr: '0.04', otherEach: '0.02', otherTotal: '0.10' },
                      { alloy: '1350', al: '99.5', fe: '0.40', si: '0.10', cu: '0.05', zn: '0.05', tiVMnCr: '0.04', otherEach: '0.03', otherTotal: '0.10' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-4 py-2 font-medium text-primary">{row.alloy}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.al}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.cu}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.tiVMnCr}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.otherEach}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.otherTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Kimyasal Kompozisyon Tablo 2: 6101 Alaşımı */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Kimyasal Kompozisyon — 6101 Alaşımı (Maks %)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Alaşım</th>
                      <th className="text-center px-4 py-2 font-semibold">Al içeriği (min %)</th>
                      <th className="text-center px-4 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-4 py-2 font-semibold">Si %</th>
                      <th className="text-center px-4 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-4 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-4 py-2 font-semibold">Mg %</th>
                      <th className="text-center px-4 py-2 font-semibold">Mn %</th>
                      <th className="text-center px-4 py-2 font-semibold">Cr %</th>
                      <th className="text-center px-4 py-2 font-semibold">B %</th>
                      <th className="text-center px-4 py-2 font-semibold">Diğer (Her) %</th>
                      <th className="text-center px-4 py-2 font-semibold">Diğer (Toplam) %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-background">
                      <td className="px-4 py-2 font-medium text-primary">6101</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">kalan</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.50</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.7</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.10</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.10</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.80</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.03</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.03</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.06</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.03</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.10</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fiziksel & Mekanik Özellikler Tablosu */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" /> Fiziksel ve Mekanik Özellikler
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Alaşım</th>
                      <th className="text-center px-3 py-2 font-semibold">Çap (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">Bobin Ağırlığı (kg)</th>
                      <th className="text-center px-3 py-2 font-semibold">Bobin Yüksekliği (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">İç Çap (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">Dış Çap (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">Çekme Mukavemeti (MPa)</th>
                      <th className="text-center px-3 py-2 font-semibold">Uzama (% min)</th>
                      <th className="text-center px-3 py-2 font-semibold">Öz. Direnç (ohm.mm²/m)</th>
                      <th className="text-center px-3 py-2 font-semibold">İletkenlik % IACS min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { alloy: '1080 H11', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '80–95', elong: '15', resist: '0.02772', conduct: '62.2' },
                      { alloy: '1350/1370 H11', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '80–95', elong: '15', resist: '0.02785', conduct: '61.9' },
                      { alloy: '1350/1370 H12', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '95–110', elong: '15', resist: '0.02794', conduct: '61.7' },
                      { alloy: '1350/1370 H13', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '105–120', elong: '15', resist: '0.02794', conduct: '61.7' },
                      { alloy: '1350/1370 H14', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '500', extDia: '1420', tensile: '115–130', elong: '15', resist: '0.0280', conduct: '61.6' },
                      { alloy: '6101 T4', dia: '9.5', weight: '2000 ± 200', height: '850–950', intDia: '500', extDia: '1500', tensile: '150 min', elong: '10', resist: '0.03260', conduct: '52.9' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.dia}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.weight}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.height}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.intDia}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.extDia}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.tensile}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.elong}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.resist}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.conduct}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-primary mb-6">Teklif Almak İster Misiniz?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Alüminyum billet ve külçe ihtiyaçlarınız için uzman ekibimiz sizinle iletişime geçsin.
            </p>
            <Button asChild size="lg" className="px-8">
              <Link to="/tr/iletisim#quote-form">Teklif Al</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
